import { useParams } from "react-router-dom";
import { EmptyState, ErrorPage, ErrorState, SkeletonStorefront } from "@branding/ui";
import { Button } from "@heroui/react";
import useSWR from "swr";
import { JsonLd } from "../components/JsonLd";
import { ProductCard } from "../components/ProductCard";
import { fetchCategory } from "../lib/api";
import { publicOrigin } from "../lib/format";
import { categoryJsonLd } from "../lib/json-ld";
import { useSsrData } from "../lib/ssr-data";
import type { Category } from "../lib/types";
import { ApiClientError } from "../lib/v2";

export function CategoryPage() {
  const { slug = "" } = useParams();
  const ssr = useSsrData();
  const fallback =
    ssr?.kind === "category" && ssr.category?.slug === slug
      ? ssr.category
      : undefined;
  const { data, error, isLoading, mutate } = useSWR<Category>(
    slug ? `kategori:${slug}` : null,
    () => fetchCategory(slug),
    { fallbackData: fallback },
  );

  if (error instanceof ApiClientError && error.status === 404) {
    return <ErrorPage code={404} actionHref="/pasar" />;
  }
  if (error) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <ErrorState
          title="Kategori tidak bisa dibuka"
          onRetry={() => {
            void mutate();
          }}
        />
      </main>
    );
  }
  if (isLoading && !data) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <SkeletonStorefront />
      </main>
    );
  }
  if (!data) return <ErrorPage code={404} actionHref="/pasar" />;

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
      <JsonLd data={categoryJsonLd(data, publicOrigin())} />
      <p className="text-sm">
        <a href="/pasar">Pasar Kupang</a>
      </p>
      <h1 className="mt-3 text-3xl font-semibold">{data.name}</h1>
      <div className="mt-8">
        {data.products.length === 0 ? (
          <EmptyState
            title="Tidak ada hasil"
            description="Belum ada produk terbit di lorong ini."
            action={
              <Button variant="primary" onPress={() => window.location.assign("/pasar")}>
                Jelajahi pasar
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
